// packages/charts/src/react/baseOptions.ts
//
// Shared ECharts option defaults — tooltip / legend / aria / text color.
// Every per-type component merges these into its own option object so the
// visual language stays consistent across chart types.
//
// All colour values go through CSS custom properties so the sandbox's
// dark-mode token overrides take effect without a re-render. Fallback hex
// values match the tokens.css defaults.
import type { EChartsOption } from "echarts";
import { formatChartValue } from "./formatValue";
import type { ChartMeta } from "./types";

export const TOOLTIP_DEFAULT = {
  backgroundColor: "var(--verity-chart-tooltip-bg, #ffffff)",
  borderColor: "var(--verity-chart-tooltip-border, rgba(0,0,0,0.08))",
  borderWidth: 1,
  textStyle: {
    color: "var(--verity-chart-tooltip-text, #1f2937)",
    fontSize: 12,
  },
  confine: true,
} as const;

export const LEGEND_DEFAULT = {
  top: 8,
  textStyle: {
    color: "var(--verity-color-text-muted, #6b7280)",
    fontSize: 11,
  },
  icon: "circle",
  itemWidth: 10,
  itemHeight: 10,
} as const;

// ECharts' `aria.decal` overlays decorative patterns (stripes, dots,
// diamonds, checker) on every series as a colour-blind fallback. It's
// well-intentioned, but layered on top of the palette it muddles the
// chart into a rummelig "patroon + kleur" look. For a consultant
// report we want clean solid fills — colour alone + a legible legend
// is already WCAG-sufficient at 3:1 contrast ratios (the palette
// meets that). `aria.enabled` stays on so screen-reader descriptions
// still fire.
export const ARIA_DEFAULT = {
  enabled: true,
  decal: { show: false },
} as const;

export const AXIS_LABEL_DEFAULT = {
  fontSize: 11,
  color: "var(--verity-color-text-muted, #6b7280)",
} as const;

export const GRID_STROKE = "var(--verity-chart-grid-stroke, rgba(0,0,0,0.08))";

/** Axis-label config for a VALUE axis, formatted from the payload's meta
 * envelope. Compact mode: a tick is a scale marker that repeats four or
 * five times down the axis, so "€500K" beats "€500.0K" and a raw "500000"
 * beats neither. */
export function valueAxisLabel(meta?: ChartMeta) {
  return {
    ...AXIS_LABEL_DEFAULT,
    formatter: (value: unknown) => formatChartValue(value, meta, "axis"),
  };
}

/** Tooltip config whose numbers are formatted the same way the server
 * formatted the tile values on the same page — full mode, no shortcuts.
 * ECharts applies `valueFormatter` inside its default tooltip templates,
 * so per-type components don't need their own formatter string. */
export function tooltipWithMeta(meta?: ChartMeta) {
  return {
    ...TOOLTIP_DEFAULT,
    valueFormatter: (value: unknown) => formatChartValue(value, meta, "full"),
  };
}

/** Tooltip for a CARTESIAN chart (line / area / bar / stacked bar).
 *
 * `trigger: "axis"` rather than ECharts' `"item"` default. With
 * `showSymbol: false` on the trend lines, an item trigger only fires when
 * the cursor lands on an invisible point — so a reader dragging along the
 * line sees nothing, and on a stacked bar they can only ever read one
 * segment at a time instead of the whole month. The axis pointer also
 * gives the comparison series a purpose: both values, one hover. */
export function cartesianTooltip(meta?: ChartMeta) {
  return {
    ...tooltipWithMeta(meta),
    trigger: "axis" as const,
    axisPointer: {
      type: "line" as const,
      lineStyle: { color: GRID_STROKE_SOLID, width: 1 },
    },
  };
}

/** Solid twin of GRID_STROKE — the axis pointer is a cursor, not a
 * gridline, so it should not read as dashed chrome. */
const GRID_STROKE_SOLID = "var(--verity-chart-grid-stroke, rgba(0,0,0,0.18))";

/** Merge base defaults (tooltip / legend / aria) onto a partial option. */
export function withBaseOptions(option: EChartsOption): EChartsOption {
  return {
    tooltip: TOOLTIP_DEFAULT,
    legend: LEGEND_DEFAULT,
    aria: ARIA_DEFAULT,
    ...option,
  };
}
